import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  BoxProps,
  Button,
  Card,
  Grid,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material';
import SouthIcon from '@mui/icons-material/South';
import NorthIcon from '@mui/icons-material/North';
import moment from 'moment';

import { TP_COLORS } from '../../../../constants';
import { useAppSelector } from '../../../../hooks';
import { getThemeMode } from '../../../../store/ConfigSlice';
import { ISubmission } from '../../../../store/SubmissionSlice';
import DetailViewCard from '../../../../components/Card/DetailViewCard';
import { getSubjectColor } from '../../../../helper';

interface StudentSubmissionListItemProps {
  submission: ISubmission;
  attempt: number;
}

const titleProps: TypographyProps = {
  variant: 'caption',
  className: 'ellipsis-text',
  sx: {
    color: 'text.primary',
  },
};
const descriptionProps: TypographyProps = {
  className: 'ellipsis-text',
  variant: 'subtitle2',
  fontWeight: 600,
  sx: {
    color: 'text.primary',
  },
};

const StudentListItem: React.FC<StudentSubmissionListItemProps> = ({
  submission,
  attempt,
}) => {
  // HOOKS
  const navigate = useNavigate();
  const params = useParams();
  const themeMode = useAppSelector(getThemeMode);
  // STYLES
  const defaultContainerStyles: BoxProps['sx'] = {
    p: '12px',
    mb: '10px',
    borderRadius: '7px',
    background:
      themeMode === 'light'
        ? TP_COLORS.listItemGray.light
        : TP_COLORS.listItemGray.dark,
  };

  let timeTaken = <Typography {...descriptionProps}>-</Typography>;

  if (
    submission.timerStart &&
    submission.timerEnd &&
    submission.isTimerEnabled &&
    submission.status === 'completed'
  ) {
    const allocatedSeconds = submission.totalScore * 60;
    const spentSeconds = (submission.timerEnd - submission.timerStart) / 1000;
    const diff = allocatedSeconds - spentSeconds;
    const diffAbs = Math.abs(allocatedSeconds - spentSeconds);

    const mins = Math.abs(Math.floor(spentSeconds / 60));
    const secs = Math.abs(Math.floor(spentSeconds % 60));

    const minText = `${mins} Min${mins > 1 ? 's' : ''}`;
    const secText = `${secs} Sec${secs > 1 ? 's' : ''}`;

    let extraMins = 0;
    let extraSecs = 0;
    let extraText = '';

    if (diff > 0 || diff < 0) {
      extraMins = Math.abs(Math.floor(diffAbs / 60));
      extraSecs = Math.abs(Math.floor(diffAbs % 60));

      extraText = `${extraMins} Min${extraMins > 1 ? 's' : ''}`;

      if (extraSecs) {
        extraText += ` ${extraSecs} Sec${extraSecs > 1 ? 's' : ''}`;
      }
    }

    let status: React.ReactElement | null = null;

    if (diff === 0) {
      status = (
        <Typography color="wraning" variant="caption">
          on time
        </Typography>
      );
    } else if (diff > 0) {
      status = (
        <Typography color="success.main" variant="caption">
          <NorthIcon color="success" fontSize="small" sx={{ height: '15px' }} />{' '}
          {extraText}
        </Typography>
      );
    } else if (diff < 0) {
      status = (
        <Typography color="error.main" variant="caption">
          <SouthIcon color="error" fontSize="small" sx={{ height: '15px' }} />{' '}
          {extraText}
        </Typography>
      );
    }

    timeTaken = (
      <Stack direction="row" alignItems="center">
        <Typography {...descriptionProps}>
          {mins > 0 ? minText : ''}
          {secs > 0 && mins > 0 ? ' ' : ''}
          {secs > 0 ? secText : ''}
          &nbsp;{status}
        </Typography>
      </Stack>
    );
  }

  return (
    <Card elevation={3} sx={{ mb: '15px' }}>
      <div style={{ padding: '15px' }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} sm={8} md={3} lg={3}>
            <DetailViewCard
              title={submission.subTopic?.topic.name || '-'}
              description={submission.subTopic?.name || '-'}
              titleProps={titleProps}
              descriptionProps={descriptionProps}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2} lg={2}>
            <DetailViewCard
              title={submission.subTopic?.topic.curriculum || '-'}
              description={submission.subTopic?.topic.subject || '-'}
              titleProps={{
                ...titleProps,
                style: { color: TP_COLORS.white },
              }}
              descriptionProps={{
                ...descriptionProps,
                style: { color: TP_COLORS.white },
              }}
              containerProps={{
                sx: {
                  background: getSubjectColor(
                    submission.subTopic?.topic.subject || ''
                  ),
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={7} md={2} lg={2}>
            <DetailViewCard
              title="Submission Date"
              description={
                submission.createdAt
                  ? moment(submission.createdAt).format('YYYY-MM-DD hh:mm A')
                  : '-'
              }
              titleProps={titleProps}
              descriptionProps={descriptionProps}
            />
          </Grid>
          <Grid item xs={12} sm={5} md={2} lg={1}>
            <DetailViewCard
              title="Score"
              description={
                submission.totalScore >= 0 && submission.score >= 0
                  ? `${submission.score
                      .toString()
                      .padStart(
                        2,
                        '0'
                      )} / ${submission.totalScore.toString().padStart(2, '0')}`
                  : '-'
              }
              titleProps={titleProps}
              descriptionProps={descriptionProps}
            />
          </Grid>
          <Grid item xs={12} sm={9} md={2} lg={3}>
            <Box sx={{ ...defaultContainerStyles }}>
              <Grid container alignItems="center">
                <Grid item xs={12}>
                  <Typography {...titleProps}>Time Taken</Typography>
                  {timeTaken}
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3} md={1} lg={1}>
            <Stack direction="row" justifyContent="center" alignItems="center">
              <Button
                variant="contained"
                color="inherit"
                size="small"
                sx={{ color: TP_COLORS.black }}
                onClick={() =>
                  navigate(
                    `${process.env.PUBLIC_URL}/dashboard/students/viewQuestions/${params.userId}/${submission._id}`
                  )
                }
              >
                View
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </div>
    </Card>
  );
};

export default StudentListItem;
